import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { BrandChip } from '@/components/BrandChip';
import { BrandSearchList } from '@/components/BrandSearchList';
import { DetailItem } from '@/components/DetailItem';
import { HintTooltip } from '@/components/HintTooltip';
import { JsonViewer } from '@/components/JsonViewer';
import { PageHeader, PageShell } from '@/components/PageShell';
import { SpaceNames } from '@/components/SpaceNames';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useCurrentUserId } from '@/db/useCurrentUserId';
import {
    allBrandsState,
    brandRequestState,
    modelSpellingState,
    modelState,
    modelsAwaitingBrandRequestState,
    pendingBrandRequestCountState,
    sharedNameplateTableState,
} from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDateTime } from '@/util/dates';
import logger from '@/util/logger';
import { brandAlreadyNaming, lineNameFromAlias, suggestBrandRequestResolution } from '@rock/brands';
import { spellingKeyFor } from '@rock/models';
import { normalizeBrandName } from '@rock/util';
import type {
    Brand,
    BrandRequest,
    BrandRequestId,
    BrandRequestSighting,
    BrandRequestSuggestion,
    Model,
    ModelId,
    UserId,
} from '@rock/types';
import { isBrandRequestOpen } from '@rock/types';
import { useSetAtom } from 'jotai';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
    BrandRequestResolutionError,
    parkRequest,
    resolveAsAlias,
    resolveAsLine,
    resolveAsNewBrand,
    resolveAsNotABrand,
    unparkRequest,
} from './brandRequestActions';
import { SharedNameplateDialog } from '@/app/shared-nameplates/SharedNameplateDialog';
import {
    RESOLUTION_KINDS,
    ResolutionBadge,
    seenAgainSummary,
    SightingCounts,
    STATUS_LABELS,
} from './BrandRequestsListPage';
import { BrandResearchCard } from './BrandResearchCard';
import { SuggestionChips } from './SuggestionChips';
import { waitingModelsFor } from './waitingModels';

const log = logger('BrandRequestDetailsPage');

export const BrandRequestDetailsPage: FC = () => {
    const navigate = useNavigate();
    const by = useCurrentUserId();
    const refreshPendingCount = useSetAtom(pendingBrandRequestCountState);
    const { requestId: rawId } = useParams<{ requestId: string }>();
    const requestId = useMemo(() => (rawId ? (decodeURIComponent(rawId) as BrandRequestId) : undefined), [rawId]);
    const [request, loadable] = useLoadableAtomValue(useMemo(() => brandRequestState(requestId), [requestId]));
    const brands = useAsyncAtomValue(allBrandsState, []);
    const sharedNameplates = useAsyncAtomValue(sharedNameplateTableState, []);
    const isOpen = !!request && isBrandRequestOpen(request);
    const suggestions = useMemo(
        () => (request && isOpen ? suggestBrandRequestResolution(request.brand, brands, sharedNameplates) : []),
        [request, isOpen, brands, sharedNameplates],
    );
    const knownAs = isOpen ? brandAlreadyNaming(request.brand, brands) : undefined;
    const [busy, setBusy] = useState(false);
    const [aliasOpen, setAliasOpen] = useState(false);
    const [notABrandOpen, setNotABrandOpen] = useState(false);
    const [sharedOpen, setSharedOpen] = useState(false);
    const [lineOpen, setLineOpen] = useState(false);
    const [lineBrand, setLineBrand] = useState<Brand>();
    const [lineName, setLineName] = useState('');

    const run = async (label: string, action: (by: UserId) => Promise<Brand | undefined>) => {
        if (!request || !by) return;
        setBusy(true);
        try {
            const brand = await action(by);
            refreshPendingCount();
            toast.success(label);
            if (brand) navigate(`/products/brands/${encodeURIComponent(brand.id)}`);
        } catch (err) {
            if (err instanceof BrandRequestResolutionError) {
                toast.error(err.message);
            } else {
                log.error('Failed to update brand request', { err, requestId: request.id });
                toast.error('Unable to update the request');
            }
        } finally {
            setBusy(false);
        }
    };
    // The alias dialog opens seeded from the chip that was clicked (its catalog name, which the search
    // list matches on), or from the strongest alias hint when opened from the button.
    const [pickedAlias, setPickedAlias] = useState<BrandRequestSuggestion>();
    const aliasSeed = pickedAlias ?? suggestions.find((s) => s.kind === 'alias');
    const aliasSeedName = aliasSeed?.brandId
        ? brands.find((b) => b.id === aliasSeed.brandId)?.canonicalName
        : undefined;

    const handlePick = (suggestion: BrandRequestSuggestion) => {
        if (suggestion.kind === 'alias') {
            setPickedAlias(suggestion);
            setAliasOpen(true);
        } else if (suggestion.kind === 'shared') {
            setSharedOpen(true);
        } else {
            setLineBrand(suggestion.brandId ? brands.find((b) => b.id === suggestion.brandId) : undefined);
            setLineOpen(true);
        }
    };
    const actionsDisabled = busy || !by;

    return (
        <PageShell width="medium">
            <PageHeader title={request?.brand ?? 'Brand request'} id={requestId} backLabel="Back to brand requests">
                {request && (
                    <Badge variant={request.status === 'pending' ? 'default' : 'secondary'}>
                        {STATUS_LABELS[request.status]}
                    </Badge>
                )}
            </PageHeader>
            {!request ? (
                <Card>
                    <CardHeader>
                        <CardTitle>{loadable.state === 'loading' ? 'Loading…' : 'Request not found'}</CardTitle>
                    </CardHeader>
                </Card>
            ) : (
                <>
                    <Card>
                        <CardHeader>
                            <CardTitle>Summary</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-4 md:grid-cols-4">
                            <DetailItem label="Seen">
                                <SightingCounts request={request} />
                            </DetailItem>
                            <DetailItem label="Spaces">
                                <SpaceNames spaceIds={request.spaceIds} />
                            </DetailItem>
                            <DetailItem label="First seen">
                                {request.firstSeenAt ? formatDateTime(request.firstSeenAt) : '—'}
                            </DetailItem>
                            <DetailItem label="Last seen">
                                {request.lastSeenAt ? formatDateTime(request.lastSeenAt) : '—'}
                            </DetailItem>
                        </CardContent>
                    </Card>

                    {request.status === 'resolved' ? (
                        <ResolutionCard request={request} brands={brands} />
                    ) : (
                        <Card>
                            <CardHeader>
                                <CardTitle>What is “{request.brand}”?</CardTitle>
                                <CardDescription>
                                    Looks like: <SuggestionChips suggestions={suggestions} onPick={handlePick} />
                                </CardDescription>
                            </CardHeader>
                            <ParkNotice request={request} />
                            {knownAs && (
                                <CardContent>
                                    <AlreadyKnownNotice
                                        name={request.brand}
                                        brand={knownAs}
                                        disabled={actionsDisabled}
                                        onResolve={() =>
                                            run(`“${request.brand}” resolved to ${knownAs.canonicalName}`, (by) =>
                                                resolveAsAlias(request, knownAs, by),
                                            )
                                        }
                                    />
                                </CardContent>
                            )}
                            <CardContent className="flex flex-wrap gap-2">
                                <HintTooltip className="cursor-default" hint={RESOLUTION_KINDS.brand.hint}>
                                    <Button
                                        disabled={actionsDisabled}
                                        onClick={() =>
                                            run(`Brand “${request.brand}” created`, (by) =>
                                                resolveAsNewBrand(request, by),
                                            )
                                        }
                                    >
                                        New brand
                                    </Button>
                                </HintTooltip>
                                <HintTooltip className="cursor-default" hint={RESOLUTION_KINDS.alias.hint}>
                                    <Button
                                        variant="outline"
                                        disabled={actionsDisabled}
                                        onClick={() => {
                                            setPickedAlias(undefined);
                                            setAliasOpen(true);
                                        }}
                                    >
                                        Alias of…
                                    </Button>
                                </HintTooltip>
                                <HintTooltip className="cursor-default" hint={RESOLUTION_KINDS.line.hint}>
                                    <Button
                                        variant="outline"
                                        disabled={actionsDisabled}
                                        onClick={() => {
                                            setLineBrand(undefined);
                                            setLineOpen(true);
                                        }}
                                    >
                                        Line of…
                                    </Button>
                                </HintTooltip>
                                <HintTooltip
                                    className="cursor-default"
                                    hint={RESOLUTION_KINDS['shared-nameplate'].hint}
                                >
                                    <Button
                                        variant="outline"
                                        disabled={actionsDisabled}
                                        onClick={() => setSharedOpen(true)}
                                    >
                                        Shared nameplate…
                                    </Button>
                                </HintTooltip>
                                <HintTooltip className="cursor-default" hint={RESOLUTION_KINDS['not-a-brand'].hint}>
                                    <Button
                                        variant="ghost"
                                        disabled={actionsDisabled}
                                        onClick={() => setNotABrandOpen(true)}
                                    >
                                        Not a brand…
                                    </Button>
                                </HintTooltip>
                                {request.status === 'parked' ? (
                                    <Button
                                        variant="ghost"
                                        disabled={actionsDisabled}
                                        onClick={() =>
                                            void run(`“${request.brand}” is back in the queue`, async () => {
                                                await unparkRequest(request);
                                                return undefined;
                                            })
                                        }
                                    >
                                        Unpark
                                    </Button>
                                ) : (
                                    <ParkButton
                                        requestId={request.id}
                                        disabled={actionsDisabled}
                                        onPark={() =>
                                            void run(`“${request.brand}” parked until it is seen again`, async (by) => {
                                                await parkRequest(request, by);
                                                return undefined;
                                            })
                                        }
                                    />
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {isOpen && (
                        <BrandResearchCard
                            request={request}
                            brands={brands}
                            by={by}
                            disabled={actionsDisabled}
                            onApply={(label, action) => void run(label, action)}
                        />
                    )}

                    {isOpen && <WaitingModelsCard requestId={request.id} />}

                    <SightingsCard sightings={request.sightings} />

                    <Card>
                        <CardHeader>
                            <CardTitle>Object</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <JsonViewer data={request} />
                        </CardContent>
                    </Card>

                    <Dialog open={lineOpen} onOpenChange={(open) => !busy && setLineOpen(open)}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>“{request.brand}” is a product line of…</DialogTitle>
                                <DialogDescription>
                                    The name resolves to the brand with its line — shown as “
                                    {lineBrand
                                        ? `${lineBrand.displayName ?? lineBrand.canonicalName} ${lineName || '…'}`
                                        : 'Brand Line'}
                                    ”. Recalls, coverage and manuals keep keying on the brand.
                                </DialogDescription>
                            </DialogHeader>
                            {!lineBrand ? (
                                <BrandSearchList
                                    brands={brands}
                                    initialQuery={suggestions.find((s) => s.kind === 'line')?.brandName ?? ''}
                                    disabled={busy}
                                    onSelect={(brand) => {
                                        setLineBrand(brand);
                                        setLineName(lineNameFromAlias(brand, request.brand) ?? request.brand);
                                    }}
                                />
                            ) : (
                                <div className="space-y-2">
                                    <div className="text-sm">
                                        Brand: <span className="font-medium">{lineBrand.canonicalName}</span>
                                        <Button
                                            variant="link"
                                            className="h-auto px-2 py-0"
                                            onClick={() => setLineBrand(undefined)}
                                        >
                                            change
                                        </Button>
                                    </div>
                                    <Input
                                        value={lineName}
                                        onChange={(event) => setLineName(event.target.value)}
                                        placeholder="Line name (e.g. Brunswick)"
                                        autoComplete="off"
                                        disabled={busy}
                                    />
                                </div>
                            )}
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setLineOpen(false)} disabled={busy}>
                                    Cancel
                                </Button>
                                <Button
                                    disabled={busy || !lineBrand || !lineName.trim()}
                                    onClick={() => {
                                        const brand = lineBrand!;
                                        setLineOpen(false);
                                        void run(
                                            `“${request.brand}” is the ${lineName.trim()} line of ${brand.canonicalName}`,
                                            (by) => resolveAsLine(request, brand, lineName.trim(), by),
                                        );
                                    }}
                                >
                                    Make it a line
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={aliasOpen} onOpenChange={(open) => !busy && setAliasOpen(open)}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>“{request.brand}” is an alias of…</DialogTitle>
                                <DialogDescription>
                                    The name will resolve to the brand you pick, and everything waiting on it links up.
                                </DialogDescription>
                            </DialogHeader>
                            <BrandSearchList
                                brands={brands}
                                initialQuery={aliasSeedName ?? ''}
                                disabled={busy}
                                onSelect={(brand) => {
                                    setAliasOpen(false);
                                    const isCanonical = normalizeBrandName(request.brand).key === brand.canonicalKey;
                                    const label = isCanonical
                                        ? `“${request.brand}” resolved to ${brand.canonicalName}`
                                        : `“${request.brand}” added as an alias of ${brand.canonicalName}`;
                                    void run(label, (by) => resolveAsAlias(request, brand, by));
                                }}
                            />
                        </DialogContent>
                    </Dialog>

                    {sharedOpen && (
                        <SharedNameplateDialog
                            open
                            onOpenChange={(open) => !open && setSharedOpen(false)}
                            initialName={request.brand}
                            fromRequestId={request.id}
                            onApplied={() => {
                                refreshPendingCount();
                                navigate('/products/shared-nameplates');
                            }}
                        />
                    )}
                    <NotABrandDialog
                        open={notABrandOpen}
                        busy={busy}
                        onOpenChange={setNotABrandOpen}
                        onConfirm={(reason) => {
                            setNotABrandOpen(false);
                            void run('Marked as not a brand', async (by) => {
                                await resolveAsNotABrand(request, reason, by);
                                return undefined;
                            });
                        }}
                    />
                </>
            )}
        </PageShell>
    );
};

/** Where the park stands: set aside for now, or back in the queue because the name was seen again. */
const ParkNotice: FC<{ request: BrandRequest }> = ({ request }) => {
    const seenAgain = seenAgainSummary(request);
    const notice =
        request.status === 'parked' && request.parked
            ? `Parked ${formatDateTime(request.parked.at)}. A new home, model or recall naming it brings it back to Pending. You can still decide now.`
            : seenAgain;
    if (!notice) return null;
    return (
        <CardContent>
            <div className="rounded-md border bg-muted/40 p-3 text-sm">{notice}</div>
        </CardContent>
    );
};

/** No confirmation, so the button itself says how many models stay held. */
const ParkButton: FC<{ requestId: BrandRequestId; disabled: boolean; onPark: () => void }> = ({
    requestId,
    disabled,
    onPark,
}) => {
    const models = useAsyncAtomValue(
        useMemo(() => modelsAwaitingBrandRequestState(requestId), [requestId]),
        [],
    );
    const waiting = waitingModelsFor(models, requestId).length;
    const held = waiting === 1 ? '1 waiting model stays held' : `${waiting} waiting models stay held`;
    return (
        <HintTooltip
            className="cursor-default"
            hint={`Not worth deciding yet. Takes the name out of the queue until a new home, model or recall names it.${waiting > 0 ? ` ${held}, and their manual hunts stay off.` : ''}`}
        >
            <Button variant="ghost" disabled={disabled} onClick={onPark}>
                Park until seen again{waiting > 0 ? ` (${waiting} waiting)` : ''}
            </Button>
        </HintTooltip>
    );
};

/** The catalog already resolves this name: settling the request needs no decision, so it takes one click. */
const AlreadyKnownNotice: FC<{ name: string; brand: Brand; disabled: boolean; onResolve: () => void }> = ({
    name,
    brand,
    disabled,
    onResolve,
}) => (
    <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 p-3 text-sm">
        <span>
            Rock already knows “{name}” as <BrandChip brand={brand} />
        </span>
        <Button size="sm" className="ml-auto" disabled={disabled} onClick={onResolve}>
            Resolve to {brand.displayName ?? brand.canonicalName}
        </Button>
    </div>
);

const ResolutionCard: FC<{ request: BrandRequest; brands: Brand[] }> = ({ request, brands }) => {
    const resolution = request.resolution;
    const brand = resolution?.brandId ? brands.find((candidate) => candidate.id === resolution.brandId) : undefined;
    return (
        <Card>
            <CardHeader>
                <CardTitle>Resolution</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <DetailItem label="Decision">
                    <ResolutionBadge request={request} brands={brands} />
                </DetailItem>
                <DetailItem label="Brand">{brand ? <BrandChip brand={brand} /> : '—'}</DetailItem>
                <DetailItem label="By">{resolution?.by ?? '—'}</DetailItem>
                <DetailItem label="At">{resolution ? formatDateTime(resolution.at) : '—'}</DetailItem>
                {resolution?.reason && (
                    <DetailItem label="Reason" className="col-span-2 md:col-span-4">
                        {resolution.reason}
                    </DetailItem>
                )}
            </CardContent>
        </Card>
    );
};

/**
 * The provisional models this name holds: each links to the brand the request resolves to, and merges into
 * that brand's model of the same number and type when there is one (`brandTwinOf`, on the server). Their
 * manual hunt waits until then.
 */
const WaitingModelsCard: FC<{ requestId: BrandRequestId }> = ({ requestId }) => {
    const models = useAsyncAtomValue(
        useMemo(() => modelsAwaitingBrandRequestState(requestId), [requestId]),
        [],
    );
    const waiting = waitingModelsFor(models, requestId);
    if (waiting.length === 0) return null;
    return (
        <Card>
            <CardHeader>
                <CardTitle>Waiting models</CardTitle>
                <CardDescription>
                    Resolving links each to the brand. One whose number and type that brand already has merges into that
                    model. Their manual hunt waits until then.
                </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2">
                {waiting.map((model) => (
                    <WaitingModelRow key={model.id} model={model} />
                ))}
            </CardContent>
        </Card>
    );
};

const WaitingModelRow: FC<{ model: Model }> = ({ model }) => {
    const spelling = useAsyncAtomValue(
        useMemo(() => modelSpellingState(spellingKeyFor(model.identity?.keyedBy, model.modelCanonical)), [model]),
        undefined,
    );
    const brands = useAsyncAtomValue(allBrandsState, []);
    const siblingIds = (spelling?.entries ?? []).map((entry) => entry.modelId).filter((id) => id !== model.id);
    return (
        <div className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm">
            <Link
                className="font-mono font-medium underline-offset-2 hover:underline"
                to={`/products/models/${encodeURIComponent(model.id)}`}
            >
                {model.modelDisplay}
            </Link>
            <EquipmentTypeBadge typeId={model.typeId} />
            {siblingIds.length > 0 && <span className="text-muted-foreground">same number:</span>}
            {siblingIds.map((id) => (
                <SiblingModelChip key={id} modelId={id} typeId={model.typeId} brands={brands} />
            ))}
        </div>
    );
};

/** A same-number model, named by its brand; marked when it is the twin a resolution to that brand merges into. */
const SiblingModelChip: FC<{ modelId: ModelId; typeId: Model['typeId']; brands: Brand[] }> = ({
    modelId,
    typeId,
    brands,
}) => {
    const sibling = useAsyncAtomValue(
        useMemo(() => modelState(modelId), [modelId]),
        undefined,
    );
    if (!sibling || sibling.archivedAt || sibling.mergedInto) return null;
    const brand = brands.find((candidate) => candidate.id === sibling.brandId);
    const twin = sibling.typeId === typeId && !!brand;
    if (!twin) {
        return (
            <Badge variant="secondary">
                {brand?.canonicalName ?? 'no brand'} · {sibling.typeId}
            </Badge>
        );
    }
    return (
        <HintTooltip
            hint={`${brand.canonicalName} already has a model of this type under this number. Resolve this request as ${brand.canonicalName} (or an alias or line of it) and the waiting model merges into that one, units and all. Resolve it as a new brand and the two stay separate.`}
        >
            <Badge>merges if resolved to {brand.canonicalName}</Badge>
        </HintTooltip>
    );
};

const SightingsCard: FC<{ sightings: BrandRequestSighting[] }> = ({ sightings }) => (
    <Card>
        <CardHeader>
            <CardTitle>Sightings</CardTitle>
            <CardDescription>Where the name showed up, most recent first.</CardDescription>
        </CardHeader>
        <CardContent>
            {sightings.length === 0 ? (
                <div className="section-description">No sightings recorded.</div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Source</TableHead>
                            <TableHead>Where</TableHead>
                            <TableHead>Space</TableHead>
                            <TableHead>Provenance</TableHead>
                            <TableHead>Evidence</TableHead>
                            <TableHead>When</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sightings.map((sighting, index) => (
                            <TableRow key={`${sighting.at}-${index}`}>
                                <TableCell>{sighting.source}</TableCell>
                                <TableCell>
                                    {sighting.modelId && (
                                        <Link
                                            className="underline-offset-2 hover:underline"
                                            to={`/products/models/${encodeURIComponent(sighting.modelId)}`}
                                        >
                                            model {sighting.modelId}
                                        </Link>
                                    )}
                                    {sighting.homeId && (
                                        <Link
                                            className="ml-2 underline-offset-2 hover:underline"
                                            to={`/directory/homes/${encodeURIComponent(sighting.homeId)}`}
                                        >
                                            home
                                        </Link>
                                    )}
                                    {sighting.recallId && (
                                        <Link
                                            className="underline-offset-2 hover:underline"
                                            to={`/products/recalls/${encodeURIComponent(sighting.recallId)}`}
                                        >
                                            recall {sighting.recallId}
                                        </Link>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <SpaceNames spaceIds={sighting.spaceId ? [sighting.spaceId] : []} />
                                </TableCell>
                                <TableCell>{sighting.provenance ?? '—'}</TableCell>
                                <TableCell>
                                    {sighting.evidenceUrl ? (
                                        <a
                                            className="underline-offset-2 hover:underline"
                                            href={sighting.evidenceUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            open
                                        </a>
                                    ) : (
                                        '—'
                                    )}
                                </TableCell>
                                <TableCell>{formatDateTime(sighting.at)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </CardContent>
    </Card>
);

const NotABrandDialog: FC<{
    open: boolean;
    busy: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: (reason: string) => void;
}> = ({ open, busy, onOpenChange, onConfirm }) => {
    const [reason, setReason] = useState('');
    return (
        <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Not a brand</DialogTitle>
                    <DialogDescription>
                        A retailer, a product word, scraper noise. The request keeps the reason; nothing waiting on it
                        links.
                    </DialogDescription>
                </DialogHeader>
                <Textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why not?" />
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button onClick={() => onConfirm(reason.trim())} disabled={busy || !reason.trim()}>
                        Mark as not a brand
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
